import { SettingsDefinitionDto } from '../../dto/setting-definition.dto';
import { moduleId } from '../module-id';
import { ApplicationSettings } from './application-settings.model';
import { Setting } from './setting';
import { SettingsField } from './settings-field.model';
import { SettingsSection } from './settings-section.model';
import { SettingsSubSection } from './settings-sub-section.model';

export class SettingsDefinition {
  public restaurant: ApplicationSettings;
  public cfd: ApplicationSettings;
  public kitchen: ApplicationSettings;

  public static fromDTO(
    settingsDefinitionDto: SettingsDefinitionDto,
    mapping: any
  ): SettingsDefinition {
    if (settingsDefinitionDto === null) {
      return null;
    }

    const settingsDefinition: SettingsDefinition = new SettingsDefinition(
      settingsDefinitionDto.oid,
      settingsDefinitionDto.settingsDefinitionVersion,
      settingsDefinitionDto.settingsDefinitionHash
    );

    const settingsDefinitionJsonParsed: any = JSON.parse(
      settingsDefinitionDto.settingsDefinitionJson
    );

    if (settingsDefinitionJsonParsed.hasOwnProperty('sections')) {
      settingsDefinition.restaurant = new ApplicationSettings({
        applicationName: 'restaurant',
        sections: settingsDefinitionJsonParsed.sections.map(
          this.mapSettingsSection,
          this
        )
      });
    } else {
      if (settingsDefinitionJsonParsed.hasOwnProperty('restaurant')) {
        settingsDefinition.restaurant = new ApplicationSettings({
          applicationName: 'restaurant',
          sections: settingsDefinitionJsonParsed['restaurant'].sections.map(
            this.mapSettingsSection,
            this
          )
        });
      }

      if (settingsDefinitionJsonParsed.hasOwnProperty('cfd')) {
        settingsDefinition.cfd = new ApplicationSettings({
          applicationName: 'cfd',
          sections: settingsDefinitionJsonParsed['cfd'].sections.map(
            this.mapSettingsSection,
            this
          )
        });
      }

      if (settingsDefinitionJsonParsed.hasOwnProperty('kitchen')) {
        settingsDefinition.kitchen = new ApplicationSettings({
          applicationName: 'kitchen',
          sections: settingsDefinitionJsonParsed['kitchen'].sections.map(
            (section: SettingsSection) => {
              return this.mapSettingsSection(section, mapping);
            }
          )
        });
      }
    }

    return settingsDefinition;
  }

  public static getDefaultKitchenSettings(): any {
    const settingsDefinitionVersion = 39;
    const settings: any = {
      settingsDefinitionVersion: settingsDefinitionVersion,
      kitchen: {
        sections: [
          {
            key: 'layoutSettings',
            label: 'Layout Settings',
            description:
              'Specify how many columns and/or rows you wish to use on your displays.',
            subsections: [
              {
                title: '',
                settings: [
                  {
                    key: 'ticketRowColumn',
                    title: 'Show kitchen items',
                    type: 'rowColumnSelect',
                    defaultValue: {
                      rows: 2,
                      columns: 4
                    },
                    max: {
                      rows: 6,
                      columns: 6
                    }
                  }
                ]
              }
            ]
          },
          {
            key: 'displaySettings',
            label: 'Display Settings',
            description:
              'Specify which orders and products appear on the kitchen display when this profile is selected.',
            subsections: [
              {
                title: '',
                settings: [
                  {
                    key: 'showKitchenItems',
                    title: 'Show kitchen items',
                    type: 'boolean',
                    visible: false
                  },
                  {
                    key: 'showBarItems',
                    title: 'Show bar items',
                    type: 'boolean',
                    visible: false
                  },
                  {
                    key: 'filterTicketsByFloor',
                    description:
                      'When this profile is selected, only orders from the specified floors will be displayed.',
                    title: 'Floors',
                    type: 'multi-checkbox',
                    defaultValue: [],
                    defaultAsync: 'floors'
                  },
                  {
                    key: 'filterTicketsByProductCategory',
                    description:
                      'When this profile is selected, only products from the specified categories will be displayed.',
                    title: 'Product Categories',
                    type: 'multi-checkbox',
                    defaultValue: [],
                    defaultAsync: 'productCategories'
                  },
                  {
                    key: 'printerFilter',
                    title: 'Printer Template Types',
                    description:
                      'When this profile is selected, only products whithin categories assigned to the specified printer template type(s) will be displayed.',
                    type: 'select',
                    defaultValue: '0',
                    items: [
                      { value: '0', name: 'Kitchen type items only' },
                      { value: '1', name: 'Bar type items only' },
                      { value: '2', name: 'Kitchen and bar type items' }
                    ]
                  },
                  {
                    key: 'itemFilter',
                    title: 'Kitchen Ticket Item Order',
                    description:
                      'When this profile is selected, the items on each ticket will be ordered by the following property.',
                    type: 'select',
                    defaultValue: '1',
                    items: [
                      { value: '0', name: 'None' },
                      { value: '1', name: 'Name' },
                      { value: '2', name: 'PLU' },
                      { value: '3', name: 'Seat' }
                    ]
                  }
                ]
              }
            ]
          },
          {
            key: 'alertSettings',
            label: 'Alert Settings',
            description:
              'Specify if new tickets cause an alert sound to play and if tickets flash on-screen after a period of inactivity.',
            subsections: [
              {
                title: '',
                settings: [
                  {
                    key: 'ringOnNewOrder',
                    title: 'New order alert sound',
                    description:
                      'Play a bell sound when a new ticket appears on the kitchen display.',
                    type: 'boolean',
                    defaultValue: false
                  },
                  {
                    key: 'blinkInNew',
                    title: "Tickets with 'new' status flash when inactive",
                    type: 'select',
                    defaultValue: '0',
                    items: [
                      { value: '0', name: 'No' },
                      { value: '1', name: 'After 1 minute' },
                      { value: '2', name: 'After 2 minutes' },
                      { value: '3', name: 'After 3 minutes' },
                      { value: '4', name: 'After 4 minutes' },
                      { value: '5', name: 'After 5 minutes' },
                      { value: '6', name: 'After 6 minutes' },
                      { value: '7', name: 'After 7 minutes' },
                      { value: '8', name: 'After 8 minutes' },
                      { value: '9', name: 'After 9 minutes' },
                      { value: '10', name: 'After 10 minutes' },
                      { value: '11', name: 'After 11 minutes' },
                      { value: '12', name: 'After 12 minutes' },
                      { value: '13', name: 'After 13 minutes' },
                      { value: '14', name: 'After 14 minutes' },
                      { value: '15', name: 'After 15 minutes' },
                      { value: '16', name: 'After 16 minutes' },
                      { value: '17', name: 'After 17 minutes' },
                      { value: '18', name: 'After 18 minutes' },
                      { value: '19', name: 'After 19 minutes' },
                      { value: '20', name: 'After 20 minutes' }
                    ]
                  }
                  // Kept as it will be added in an issue following HOS-16076
                  // {
                  //   key: 'blinkInCooking',
                  //   title: "Tickets with 'cooking' status flash when inactive",
                  //   type: 'select',
                  //   defaultValue: '0',
                  //   items: [
                  //     { value: '0', name: 'No' },
                  //     { value: '1', name: 'After 1 minute' },
                  //     { value: '3', name: 'After 3 minutes' },
                  //     { value: '5', name: 'After 5 minutes' },
                  //     { value: '10', name: 'After 10 minutes' },
                  //     { value: '15', name: 'After 15 minutes' }
                  //   ]
                  // }
                ]
              }
            ]
          }
        ]
      }
    };
    return {
      settingsDefinitionVersion: settingsDefinitionVersion,
      moduleName: moduleId.KITCHEN,
      settingsDefinitionJson: JSON.stringify(settings)
    };
  }

  constructor(
    public oid: number,
    public settingsDefinitionVersion: number,
    public settingsDefinitionHash: number
  ) {}

  private static mapSettingsSection(
    section: SettingsSection,
    mapping: any
  ): SettingsSection {
    return new SettingsSection(
      section.key,
      section.label,
      section.description,
      section.subsections.map((subsection: SettingsSubSection) => {
        return this.mapSettingsSubSection(subsection, mapping);
      })
    );
  }

  private static mapSettingsSubSection(
    subsection: SettingsSubSection,
    mapping: any
  ): SettingsSubSection {
    return new SettingsSubSection(
      subsection.label,
      subsection.settings.map((setting: any) => {
        return this.mapSettingsField(setting, mapping);
      })
    );
  }

  private static mapSettingsField(
    setting: Setting,
    mapping: any
  ): SettingsField {
    if (setting.defaultValue === '[]') {
      setting.defaultValue = [];
    }
    if (setting.defaultAsync && mapping[setting.defaultAsync]) {
      if (setting.type === 'multi' || setting.type === 'multi-checkbox') {
        setting.items = mapping[setting.defaultAsync]();
        setting.multiSelectSearch = true;
      }
    }
    return new SettingsField(setting);
  }
}
